/**
 * Logger Utility
 * 
 * Purpose: Prevent sensitive data exposure in production
 * 
 * Security Benefits:
 * - Console logs only show in development
 * - Sensitive data not exposed to users
 * - Production logs are silent
 * - Can easily add external logging service
 * 
 * Usage:
 * import { logger } from '@/shared/utils/helpers/logger';
 * 
 * logger.log('User logged in:', userId);  // Only in dev
 * logger.error('API error:', error);      // Only in dev
 * logger.warn('Deprecated function');     // Only in dev
 * 
 * Replace all console.log with logger.log for security
 */

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Safe logger that only logs in development mode
 * Production logs are completely silent
 */
export const logger = {
    /**
     * Log general information (dev only)
     * @param {...any} args - Arguments to log
     */
    log: (...args) => {
        if (isDevelopment) {
            console.log(...args);
        }
    },

    /**
     * Log errors (dev only)
     * In production, you should send errors to external service
     * @param {...any} args - Arguments to log
     */
    error: (...args) => {
        if (isDevelopment) {
            console.error(...args);
        } else {
            // TODO: Send to error tracking service (Sentry, LogRocket, etc.)
            // sendToErrorService(args);
        }
    },

    /**
     * Log warnings (dev only)
     * @param {...any} args - Arguments to log
     */
    warn: (...args) => {
        if (isDevelopment) {
            console.warn(...args);
        }
    },

    /**
     * Log debug information (dev only)
     * @param {...any} args - Arguments to log
     */
    debug: (...args) => {
        if (isDevelopment) {
            console.debug(...args);
        }
    },

    /**
     * Log table data (dev only)
     * @param {any} data - Data to display as table
     */
    table: (data) => {
        if (isDevelopment) {
            console.table(data);
        }
    },

    /**
     * Start a timer (dev only)
     * @param {string} label - Timer label
     */
    time: (label) => {
        if (isDevelopment) {
            console.time(label);
        }
    },

    /**
     * End a timer (dev only)
     * @param {string} label - Timer label
     */
    timeEnd: (label) => {
        if (isDevelopment) {
            console.timeEnd(label);
        }
    },

    /**
     * Group logs (dev only)
     * @param {string} label - Group label
     */
    group: (label) => {
        if (isDevelopment) {
            console.group(label);
        }
    },

    /**
     * End log group (dev only)
     */
    groupEnd: () => {
        if (isDevelopment) {
            console.groupEnd();
        }
    },
};

/**
 * Sanitize sensitive data before logging
 * Use this to remove passwords, tokens, etc.
 * 
 * @param {object} obj - Object to sanitize
 * @returns {object} Sanitized object
 * 
 * @example
 * const userData = { name: 'John', password: '123456', jwt: 'token...' };
 * logger.log('User:', sanitize(userData));
 * // Output: { name: 'John', password: '[REDACTED]', jwt: '[REDACTED]' }
 */
export const sanitize = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;

    const sensitiveKeys = ['password', 'jwt', 'token', 'secret', 'apiKey', 'api_key', 'accessToken', 'refreshToken'];
    
    const sanitized = { ...obj };
    
    for (const key in sanitized) {
        if (sensitiveKeys.some(sensitive => key.toLowerCase().includes(sensitive.toLowerCase()))) {
            sanitized[key] = '[REDACTED]';
        } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
            sanitized[key] = sanitize(sanitized[key]);
        }
    }
    
    return sanitized;
};

/**
 * Safe error message for users
 * Never expose internal errors to end users
 * 
 * @param {Error|object} error - Error object
 * @returns {string} Safe user-friendly message
 * 
 * @example
 * try {
 *   await apiCall();
 * } catch (error) {
 *   logger.error('API Error:', error); // Log full error (dev only)
 *   showSnackbar(getSafeErrorMessage(error)); // Show safe message to user
 * }
 */
export const getSafeErrorMessage = (error) => {
    // Log full error for developers
    logger.error('Full error:', error);
    
    // Return generic message to users
    const errorMap = {
        400: 'Invalid request. Please check your input.',
        401: 'Please log in again to continue.',
        403: 'You don\'t have permission to perform this action.',
        404: 'The requested resource was not found.',
        429: 'Too many requests. Please wait a moment.',
        500: 'Server error. Please try again later.',
        502: 'Service temporarily unavailable.',
        503: 'Service temporarily unavailable.',
    };
    
    const statusCode = error?.response?.status || error?.status || error?.code;
    
    return errorMap[statusCode] || 'An unexpected error occurred. Please try again.';
};

/**
 * Production-safe logger that can be used for critical production logs
 * These will show in production (use sparingly!)
 * 
 * @example
 * productionLogger.criticalError('Payment processing failed', { orderId: 123 });
 */
export const productionLogger = {
    /**
     * Log critical errors that need immediate attention
     * These WILL show in production
     */
    criticalError: (...args) => {
        console.error('[CRITICAL]', ...args);
        // TODO: Send to error tracking service
        // sendToErrorService({ level: 'critical', message: args });
    },

    /**
     * Log important info (production-safe)
     */
    info: (...args) => {
        console.info('[INFO]', ...args);
    },
};

export default logger;
